// packages/frontend/src/components/CreateCotation/FirebaseUploadComponent.tsx
import { FC, useCallback, useEffect, useState } from 'react';

import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Button, CircularProgress, Grid, Snackbar, Typography } from '@mui/material';
import { LinearProgress } from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { deleteObject, getDownloadURL, getStorage, ref, uploadBytesResumable } from 'firebase/storage';

import { extractFileName, isPdf } from '../../utils';
import { PdfCompressionError, compressPdfToFit, needsPdfCompression } from '../../utils/compressPdf';
import { formatMegabytes, getUploadRejectionReason } from '../../utils/firebaseUpload';
import Dropzone from './DropZone';

const storage = getStorage();

/**
 * `/api/img/resize` renvoie du JPEG : le nom doit suivre, sinon une photo
 * `chassis.webp` est stockée sous une extension qui ment sur son contenu et
 * casse l'aperçu comme le téléchargement côté back-office.
 */
const toJpegFileName = (fileName: string): string => `${fileName.replace(/\.[^./\\]+$/, '')}.jpg`;

interface FirebaseUploadComponentProps {
  onUploadComplete?: (url: string) => void;
  onDeleteComplete?: (url: string) => void;
  uploadedUrls?: string[];
  isRequired?: boolean;
  onValidate?: (isValid: boolean) => void;
  source?: string;
  /** Contenu rendu entre la dropzone et les previews (ex: loader OCR). */
  afterDropzoneSlot?: React.ReactNode;
  /**
   * Appelé AVANT la suppression de l'objet Firebase Storage. S'il rejette, la
   * suppression est annulée et le message de l'erreur est affiché. Sert aux
   * modales de démarche à retirer d'abord la référence en base (périmètre
   * CFN) : supprimer l'objet en premier laisserait au coffre une URL morte.
   */
  onBeforeDelete?: (url: string) => Promise<void>;
  /** Liste seule (previews + croix), sans zone de dépôt : retirer sans déposer. */
  hideDropzone?: boolean;
}

const FirebaseUploadComponent: FC<FirebaseUploadComponentProps> = ({
  onUploadComplete,
  onDeleteComplete,
  uploadedUrls,
  isRequired = false,
  onValidate,
  source,
  afterDropzoneSlot,
  onBeforeDelete,
  hideDropzone = false,
}) => {
  const [urls, setUrls] = useState<string[]>(uploadedUrls || []);
  const [loading, setLoading] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'error' | 'info'>('error');
  const [progress, setProgress] = useState<number>(0);
  const [deletingUrls, setDeletingUrls] = useState<Set<string>>(new Set());

  const auth = getAuth();
  const currentUser = auth.currentUser;

  /** Affiche un message dans le bandeau, sans le traiter comme une erreur technique. */
  const notify = (message: string, severity: 'error' | 'info') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  /**
   * Trace un fichier écarté AVANT l'envoi. Sans ça, brancher le garde-fou
   * ferait disparaître les `storage/unauthorized` de Sentry et on perdrait la
   * mesure du problème au moment précis où on cherche à vérifier qu'il recule.
   */
  const reportRejection = (file: File, reason: string) => {
    Sentry.withScope((scope) => {
      scope.setLevel('info');
      scope.setTag('upload_rejected_client_side', 'true');
      scope.setExtras({ fileName: file.name, fileSize: file.size, fileType: file.type, reason });
      Sentry.captureMessage(`Dépôt refusé avant envoi : ${reason}`);
    });
  };

  const handleError = (error: Error, message: string, extraInfo: Record<string, unknown> = {}) => {
    setSnackbarMessage(message);
    setSnackbarSeverity('error');
    setSnackbarOpen(true);
    console.error(message, error);

    // Vérifier si c'est une erreur Firebase Storage
    const isFirebaseStorageError =
      error.message?.includes('storage/unauthorized') || error.message?.toLowerCase().includes('firebase storage');

    Sentry.withScope((scope) => {
      scope.setExtras({
        ...extraInfo,
        isFirebaseStorageError,
        errorCode: (error as any).code,
        storageErrorDetails: {
          path: extraInfo.fileName,
          userId: currentUser?.uid,
          userEmail: currentUser?.email,
          timestamp: new Date().toISOString(),
        },
      });

      if (currentUser) {
        scope.setUser({
          id: currentUser.uid,
          email: currentUser.email ?? '',
          metadata: {
            lastSignInTime: currentUser.metadata.lastSignInTime,
            creationTime: currentUser.metadata.creationTime,
          },
        });
      }

      // Forcer l'activation du replay pour les erreurs Firebase Storage
      if (isFirebaseStorageError) {
        Sentry.setTag('storage_error', 'true');
        scope.setTag('error_type', 'firebase_storage');
        // Sentry.startSession(); // Force une nouvelle session
      }

      Sentry.captureException(error);
    });
  };

  useEffect(() => {
    if (isRequired && onValidate) {
      const isValid = urls?.length > 0;
      onValidate(isValid);
    }
  }, [urls, isRequired, onValidate]);

  const handleDelete = async (urlToDelete: string): Promise<void> => {
    if (deletingUrls.has(urlToDelete)) {
      return; // Éviter les suppressions doubles
    }

    const fileName = urlToDelete.split('/').pop()?.split('?')[0];
    if (fileName) {
      setDeletingUrls((prev) => new Set([...prev, urlToDelete]));

      const storageRef = ref(storage, `${decodeURIComponent(fileName)}`);
      try {
        // La référence en base d'abord : un refus (dossier déjà archivé) doit
        // laisser le fichier intact.
        if (onBeforeDelete) {
          try {
            await onBeforeDelete(urlToDelete);
          } catch (error) {
            notify(error instanceof Error ? error.message : 'Impossible de retirer la pièce du dossier.', 'error');
            return;
          }
        }
        await deleteObject(storageRef);
        setUrls((prevUrls) => prevUrls.filter((url) => url !== urlToDelete));
        console.log(`Image deleted: ${urlToDelete}`);
        onDeleteComplete?.(urlToDelete);
      } catch (error: any) {
        const errorMessage = "Erreur lors de la suppression de l'image";
        const extraInfo = { url: urlToDelete, fileName: fileName };
        handleError(error, errorMessage, extraInfo);
      } finally {
        setDeletingUrls((prev) => {
          const newSet = new Set(prev);
          newSet.delete(urlToDelete);
          return newSet;
        });
      }
    }
  };

  const handleClose = (): void => {
    setSnackbarOpen(false);
  };

  /**
   * Met un fichier en état d'être accepté par Firebase :
   *  - PDF : compressé dans le navigateur s'il dépasse la limite Storage ;
   *  - image : redimensionnée par `/api/img/resize`, qui renvoie du JPEG.
   */
  const prepareFileForUpload = async (file: File): Promise<File> => {
    if (isPdf(file)) {
      if (needsPdfCompression(file)) {
        notify(
          `Document volumineux (${formatMegabytes(file.size)}), compression en cours. Merci de patienter.`,
          'info',
        );
      }
      return compressPdfToFit(file);
    }

    const formData = new FormData();
    formData.append('image', file);

    const response = await axios.post('/api/img/resize', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      responseType: 'blob',
    });

    // `/api/img/resize` répond TOUJOURS du JPEG (sharp `.jpeg()`), quel que soit
    // le format entrant. Réétiqueter le résultat avec le type d'origine faisait
    // repartir un JPEG déguisé en `image/webp`, `image/svg+xml` ou
    // `image/x-adobe-dng` — refusé par les Security Rules, qui n'acceptent que
    // PNG, JPEG, HEIC et PDF. C'était la moitié des `storage/unauthorized`
    // relevés en août 2026 (ewigo, biwiz).
    return new File([response.data], toJpegFileName(file.name), { type: 'image/jpeg' });
  };

  const handleFilesAdded = useCallback(
    async (files: File[]) => {
      setLoading(true);
      let uploadsStarted = 0;

      // Séquentiel et non `forEach` : la compression d'un PDF est gourmande, et
      // deux scans de 25 Mo rasterisés en parallèle font ramer l'onglet.
      for (const file of files) {
        try {
          const prepared = await prepareFileForUpload(file);

          // Dernier filtre avant Firebase : au-delà de cette ligne, un refus
          // revient en `storage/unauthorized`, que l'agence ne peut pas
          // interpréter.
          const rejection = getUploadRejectionReason(prepared);
          if (rejection) {
            reportRejection(prepared, rejection);
            notify(rejection, 'error');
            continue;
          }

          uploadFile(prepared, source);
          uploadsStarted += 1;
        } catch (error) {
          if (error instanceof PdfCompressionError) {
            reportRejection(file, error.message);
            notify(error.message, 'error');
            continue;
          }
          const errorMessage = "Erreur lors du redimensionnement de l'image";
          const extraInfo = { fileSize: file.size, fileType: file.type, fileName: file.name };
          handleError(error as Error, errorMessage, extraInfo);
        }
      }

      // `uploadFile` rend la main sur `loading` à la fin de l'envoi. Quand rien
      // n'est parti, personne ne le fait : sans ça la zone reste en chargement
      // avec le message d'erreur affiché par-dessus.
      if (uploadsStarted === 0) {
        setLoading(false);
      }
    },
    [source],
  ); // Add source to dependencies

  function getStorageRef(file: File, source?: string) {
    const uniqueId = Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
    const fileExtension = file.name.split('.').pop();

    if (!fileExtension) {
      throw new Error('File extension is missing');
    }

    const fileNameWithoutExtension = file.name
      .slice(0, -(fileExtension.length + 1))
      .replace(/\s+/g, '_')
      .replace(/[^a-zA-Z0-9_\-.]/g, '');

    if (!isPdf(file.name)) {
      const randomFileName = `${uniqueId}.${fileExtension}`;
      return ref(storage, `images/${source ?? currentUser?.uid}/${randomFileName}`);
    } else {
      const randomSuffix = Math.floor(Math.random() * 90000) + 10000; // Génère un nombre aléatoire de 5 chiffres
      const randomFileName = `${fileNameWithoutExtension}_${randomSuffix}.${fileExtension}`;
      return ref(storage, `images/${source ?? currentUser?.uid}/${randomFileName}`);
    }
  }

  const uploadFile = (file: File, source?: string): void => {
    try {
      const storageRef = getStorageRef(file, source); // Pass source here
      const uploadTask = uploadBytesResumable(storageRef, file);

      const uploadPromise = new Promise<string>((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setProgress(progress);
          },
          (error) => {
            console.error('Upload error:', error);
            reject(error);
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              resolve(downloadURL);
            } catch (error) {
              console.error('Error getting download URL:', error);
              reject(error);
            }
          },
        );
      });

      uploadPromise
        .then((downloadURL) => {
          setUrls((prevUrls) => [...prevUrls, downloadURL]);
          setLoading(false);
          onUploadComplete?.(downloadURL);
        })
        .catch((error) => {
          const errorMessage = `Erreur lors de l'upload de l'image : ${error.message}`;
          const extraInfo = { fileSize: file.size, fileType: file.type };
          handleError(error, errorMessage, extraInfo);
        });
    } catch (error) {
      if (error instanceof Error) {
        const errorMessage = `Erreur lors de l'upload de l'image : ${error.message}`;
        const extraInfo = {
          fileSize: file.size,
          fileType: file.type,
          fileName: file.name,
        };
        handleError(error, errorMessage, extraInfo);
      }
    }
  };

  const checkImageUrl = async (url: string): Promise<boolean> => {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      return response.ok;
    } catch {
      return false;
    }
  };

  const handleImageError = async (url: string): Promise<void> => {
    const isValid = await checkImageUrl(url);
    if (!isValid) {
      setUrls((prevUrls) => prevUrls.filter((u) => u !== url));
      regenerateUrl(url);
    }
  };

  const regenerateUrl = async (oldUrl: string): Promise<void> => {
    try {
      const fileName = oldUrl.split('/').pop()?.split('?')[0];
      if (fileName) {
        const storageRef = ref(storage, `${decodeURIComponent(fileName)}`);

        await deleteObject(storageRef);

        const file = await fetch(oldUrl)
          .then((res) => res.blob())
          .then((blob) => new File([blob], fileName));
        const newStorageRef = getStorageRef(file);
        const uploadTask = uploadBytesResumable(newStorageRef, file);

        const newDownloadURL = await new Promise<string>((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            null,
            (error) => reject(error),
            async () => {
              try {
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                resolve(downloadURL);
              } catch (error) {
                reject(error);
              }
            },
          );
        });

        setUrls((prevUrls) => prevUrls.map((url) => (url === oldUrl ? newDownloadURL : url)));
        onUploadComplete?.(newDownloadURL);
      }
    } catch (error: any) {
      const errorMessage = "Erreur lors de la régénération de l'URL";
      const extraInfo = { oldUrl, fileName: oldUrl.split('/').pop() };
      handleError(error, errorMessage, extraInfo);
    }
  };

  const renderPreview = (url: string, index: number) => {
    if (isPdf(url)) {
      const fileName = extractFileName(url);
      return (
        <Grid
          style={{
            height: '100%',
            overflow: 'hidden',
            objectPosition: 'center',
            objectFit: 'cover',
            background: 'rgba(253, 187, 95, 0.4)',
            width: 190,
            maxWidth: '100%',
            flex: 'inherit',
            position: 'relative',
          }}
          item
          xs={12}
          sm={6}
          md={4}
          key={index}
        >
          <PictureAsPdfIcon
            style={{
              fontSize: 10,
              height: 'auto',
              width: '70px',
              color: 'black',
              marginTop: '10px',
              marginLeft: '10px',
            }}
          />
          <Typography
            sx={{
              fontFamily: 'Urbanist',
              fontSize: '8px',
              textDecoration: 'none',
              color: 'black',
              textAlign: 'center',
              position: 'absolute',
              bottom: 10,
              left: '50%',
              transform: 'translateX(-50%)',
            }}
          >
            {fileName.split('/').pop()}
          </Typography>
        </Grid>
      );
    } else {
      return <img src={url} alt="" width="190" onError={() => handleImageError(url)} />;
    }
  };

  return (
    <div>
      {!hideDropzone && <Dropzone onFilesAdded={handleFilesAdded} />}
      {loading && (
        <LinearProgress
          sx={{
            marginTop: 5,
            height: '10px',
            '& .MuiLinearProgress-bar': {
              backgroundColor: '#FDBB5F',
            },
          }}
          variant="determinate"
          value={progress}
        />
      )}
      {afterDropzoneSlot}
      <Grid item xs={12} sx={{ marginTop: 2, display: 'flex', flexFlow: 'row wrap' }}>
        {urls.map((url, index) => (
          <div
            key={index}
            style={{
              overflow: 'hidden',
              height: 190,
              width: 190,
              position: 'relative',
              marginRight: 13,
              marginBottom: 13,
              border: '1px solid lightgray',
            }}
          >
            {deletingUrls.has(url) ? (
              <div
                style={{
                  position: 'relative',
                  height: '100%',
                  width: '100%',
                  opacity: 0.5,
                  cursor: 'not-allowed',
                }}
              >
                {renderPreview(url, index)}
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1,
                  }}
                >
                  <CircularProgress size={40} style={{ color: '#FDBB5F' }} />
                </div>
              </div>
            ) : (
              <a href={url} target="_blank" rel="noopener noreferrer">
                {renderPreview(url, index)}
              </a>
            )}
            <Button
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                background: 'red',
                color: 'white',
                minWidth: '36px',
                width: '36px',
                height: '36px',
                padding: 0,
                zIndex: 2,
              }}
              onClick={() => handleDelete(url)}
              disabled={deletingUrls.has(url)}
            >
              {deletingUrls.has(url) ? <CircularProgress size={20} style={{ color: 'white' }} /> : 'X'}
            </Button>
          </div>
        ))}
      </Grid>
      {/* Une erreur reste affichée jusqu'à fermeture : ces messages disent quoi
          refaire (rescanner, changer de format) et six secondes ne suffisent pas
          à les lire. */}
      <Snackbar open={snackbarOpen} autoHideDuration={snackbarSeverity === 'info' ? 6000 : null} onClose={handleClose}>
        <MuiAlert elevation={6} variant="filled" onClose={handleClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

export default FirebaseUploadComponent;
